/**
 * RoomGuestLanding (/rg/:token) — a friend of the room booker scans the booker's group QR. We attach
 * them to the stay with LIMITED access (call staff + chat only) and open the services panel in guest
 * mode. Only works while the booker is checked in; dies at checkout.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BedDouble, RefreshCw, ConciergeBell } from 'lucide-react';
import { apiService } from '@/services/api';
import RoomServicePanel from '@/pages/Customer/Rooms/RoomServicePanel';

type Phase = 'loading' | 'ready' | 'unavailable' | 'error';

const getDeviceId = (): string => {
  try {
    let id = localStorage.getItem('qrDeviceId');
    if (!id) {
      id = window.crypto?.randomUUID?.() || `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('qrDeviceId', id);
    }
    return id;
  } catch {
    return `dev_${Math.random().toString(36).slice(2)}`;
  }
};

const RoomGuestLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [roomLabel, setRoomLabel] = useState('');
  const [branchName, setBranchName] = useState('');
  const [organizerName, setOrganizerName] = useState('');
  const [organizerLogo, setOrganizerLogo] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [showPanel, setShowPanel] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) { setPhase('error'); setMessage('Invalid QR code.'); return; }
    let cancelled = false;
    (async () => {
      setPhase('loading');
      try {
        const infoRes = await apiService.getRoomShare(token);
        const info = infoRes?.data || {};
        if (cancelled) return;
        setRoomLabel(info.roomLabel || 'Your room');
        setBranchName(info.branchName || '');
        if (info.organizerName) setOrganizerName(info.organizerName);
        if (info.organizerLogoUrl) setOrganizerLogo(info.organizerLogoUrl);
        if (info.branchId) setBranchId(info.branchId);
        if (info.active === false) {
          setPhase('unavailable');
          setMessage('This room code is not active yet. Ask the room booker once they have checked in.');
          return;
        }
        const sesRes = await apiService.createRoomShareSession(token, { deviceId: getDeviceId() });
        const d = sesRes?.data || {};
        if (cancelled) return;
        if (!d.accessToken || !d.bookingId) throw new Error('Could not join the room.');
        apiService.setGuestTokens(d.accessToken, d.refreshToken); // tab-scoped: see api.ts getTokens()
        setBookingId(d.bookingId);
        setBranchId(d.branchId || info.branchId || '');
        setRoomLabel(d.roomLabel || info.roomLabel || 'Your room');
        setShowPanel(true);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setPhase(/not active/i.test(String((e as Error)?.message)) ? 'unavailable' : 'error');
        setMessage(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, attempt]);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.brand}>
          <div style={s.brandDot}>{organizerLogo ? <img src={organizerLogo} alt="" style={s.brandLogo} /> : <BedDouble size={22} color="#fff" />}</div>
          <div style={{ minWidth: 0 }}>
            <div style={s.brandName}>{organizerName || branchName || 'Hotel'}</div>
            <div style={s.brandSub}>{roomLabel}{branchName && organizerName ? ` · ${branchName}` : ''}</div>
          </div>
        </div>

        {phase === 'loading' && <div style={s.center}><div style={s.spinner} /><p style={s.muted}>Joining the room…</p></div>}
        {(phase === 'unavailable' || phase === 'error') && (
          <div style={s.center}>
            <ConciergeBell size={34} color="#94a3b8" />
            <p style={s.muted}>{message}</p>
            <button style={s.retry} onClick={() => setAttempt((a) => a + 1)}><RefreshCw size={16} /> Try again</button>
          </div>
        )}
        {phase === 'ready' && !showPanel && (
          <div style={s.center}>
            <p style={s.muted}>You can call staff or chat any time.</p>
            <button style={s.retry} onClick={() => setShowPanel(true)}><ConciergeBell size={16} /> Room services</button>
          </div>
        )}
      </div>

      {phase === 'ready' && showPanel && bookingId && (
        <RoomServicePanel bookingId={bookingId} branchId={branchId} roomLabel={roomLabel} accessLevel="guest" onClose={() => setShowPanel(false)} />
      )}
      <style>{`@keyframes qrspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'linear-gradient(160deg,#0b0f17,#131c2b)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box' },
  card: { width: 'min(440px, 100%)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 22, color: '#e5e7eb', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  brand: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  brandDot: { width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#0ea5e9,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  brandLogo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  brandName: { fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  brandSub: { fontSize: '0.85rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '18px 6px' },
  muted: { color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.4 },
  spinner: { width: 34, height: 34, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.15)', borderTopColor: '#0ea5e9', animation: 'qrspin 0.8s linear infinite' },
  retry: { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontWeight: 700, cursor: 'pointer' },
};

export default RoomGuestLanding;
